// Compact card shown in a kanban column — click to navigate, dropdown to transition.
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  RepairTicket,
  STATUS_LABELS,
  STATUS_COLORS,
  ALLOWED_TRANSITIONS,
  fmtVND,
  repairApi,
} from "../repair-api";

interface Props {
  ticket: RepairTicket;
}

export function TicketCard({ ticket }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const allowed = ALLOWED_TRANSITIONS[ticket.status] ?? [];

  const transition = useMutation({
    mutationFn: (s: string) => repairApi.transition(ticket.id, s),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["repair-kanban"] });
      qc.invalidateQueries({ queryKey: ["repair-ticket", ticket.id] });
    },
  });

  function handleCardClick(e: React.MouseEvent) {
    // Don't navigate when clicking the dropdown.
    if ((e.target as HTMLElement).closest("select")) return;
    navigate({ to: `/repair/${ticket.id}` as string });
  }

  function handleTransition(e: React.ChangeEvent<HTMLSelectElement>) {
    const next = e.target.value;
    if (next) transition.mutate(next);
    e.target.value = "";
  }

  return (
    <div
      className="rounded-lg border bg-card p-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow space-y-2"
      onClick={handleCardClick}
    >
      {/* Code + badge */}
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs font-semibold text-primary">
          {ticket.code}
        </span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_COLORS[ticket.status] ?? ""}`}>
          {STATUS_LABELS[ticket.status] ?? ticket.status}
        </span>
      </div>

      {/* Device */}
      <p className="text-sm font-medium leading-tight line-clamp-2">
        {ticket.deviceDesc}
      </p>

      {/* Customer */}
      {ticket.customerName && (
        <p className="text-xs text-muted-foreground truncate">
          {ticket.customerName}
          {ticket.customerPhone ? ` · ${ticket.customerPhone}` : ""}
        </p>
      )}

      {/* Cost + transition */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <span className="text-xs font-semibold text-foreground">
          {fmtVND(ticket.finalCost)}₫
        </span>

        {allowed.length > 0 && (
          <select
            className="rounded border bg-background px-1.5 py-0.5 text-xs text-foreground focus:outline-none"
            defaultValue=""
            onChange={handleTransition}
            disabled={transition.isPending}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="" disabled>Chuyển...</option>
            {allowed.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s] ?? s}</option>
            ))}
          </select>
        )}
      </div>

      {transition.isError && (
        <p className="text-[10px] text-destructive">Lỗi chuyển trạng thái</p>
      )}
    </div>
  );
}
